import React from 'react';
import { motion } from 'framer-motion';
import { Database, Cloud, LayoutGrid, ShieldCheck, Share2, PieChart, ArrowRight, Activity, Server, Lock } from 'lucide-react';
export function ArchitectureDiagram() {
  return <section className="relative w-full bg-[#16120F] py-24 md:py-32 overflow-hidden min-h-[900px] flex items-center" data-id="element-599" data-mp="3svurylyarti6">
      {/* Background SVG Lines */}
      <div className="absolute inset-0 z-0 opacity-60" data-id="element-600" data-mp="3oxndjxrb34a5">
        <svg className="w-full h-full" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg" data-id="element-601" data-mp="3jwzkl6jiodda">
          <defs data-id="element-602" data-mp="3f46gmkvvh929">
            <linearGradient id="glow" x1="0%" y1="0%" x2="100%" y2="0%" data-id="element-603" data-mp="3f3endgpqznf5">
              <stop offset="0%" stopColor="#EA580C" stopOpacity="0" data-id="element-604" data-mp="3a6xuyabimmw9" />
              <stop offset="50%" stopColor="#EA580C" stopOpacity="1" data-id="element-605" data-mp="35834qnb7xw24" />
              <stop offset="100%" stopColor="#EA580C" stopOpacity="0" data-id="element-606" data-mp="315q8cpid3ai0" />
            </linearGradient>
            <filter id="blur" data-id="element-607" data-mp="2w6n7lh9ezqfu">
              <feGaussianBlur stdDeviation="4" data-id="element-608" data-mp="2w7ogpg6ve2ux" />
            </filter>
          </defs>

          {/* Base Lines */}
          <path d="M -100 400 L 200 400 L 300 200 L 500 200 L 600 400 L 800 400" fill="none" stroke="#332A24" strokeWidth="2" data-id="element-609" data-mp="21tgcjnns5zj6" />
          <path d="M 500 200 L 700 200 L 800 100 L 1000 100" fill="none" stroke="#332A24" strokeWidth="2" data-id="element-610" data-mp="26n1cvmcuj5lj" />
          <path d="M 800 400 L 1000 400 L 1100 600 L 1300 600" fill="none" stroke="#332A24" strokeWidth="2" data-id="element-611" data-mp="1srql4htwf0le" />
          <path d="M 400 700 L 600 700 L 700 500 L 800 500" fill="none" stroke="#332A24" strokeWidth="2" data-id="element-612" data-mp="2kkpv0mfj3ymw" />
          <path d="M 800 500 L 800 700 L 1000 700" fill="none" stroke="#332A24" strokeWidth="2" data-id="element-613" data-mp="2pjkc9jxulf4w" />

          {/* Animated Glowing Lines */}
          <motion.path d="M -100 400 L 200 400 L 300 200 L 500 200" fill="none" stroke="url(#glow)" strokeWidth="3" filter="url(#blur)" initial={{
          pathLength: 0,
          opacity: 0
        }} animate={{
          pathLength: 1,
          opacity: 1
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'linear'
        }} data-id="element-614" data-mp="2bj9mylc6s8lu" />
          <motion.path d="M 500 200 L 700 200 L 800 100 L 1000 100" fill="none" stroke="url(#glow)" strokeWidth="3" filter="url(#blur)" initial={{
          pathLength: 0,
          opacity: 0
        }} animate={{
          pathLength: 1,
          opacity: 1
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'linear',
          delay: 1
        }} data-id="element-615" data-mp="36tejgye7eebt" />
          <motion.path d="M 800 400 L 1000 400 L 1100 600 L 1300 600" fill="none" stroke="url(#glow)" strokeWidth="3" filter="url(#blur)" initial={{
          pathLength: 0,
          opacity: 0
        }} animate={{
          pathLength: 1,
          opacity: 1
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'linear',
          delay: 2
        }} data-id="element-616" data-mp="2o36mky13kk42" />
        </svg>
      </div>

      <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-10 w-full grid grid-cols-1 lg:grid-cols-[400px_1fr] gap-12" data-id="element-617" data-mp="cbt6jabez2ew">
        {/* Left Content */}
        <div className="flex flex-col justify-center pt-10 lg:pt-0" data-id="element-618" data-mp="1q9dyp84110zj">
          <h2 className="text-5xl md:text-7xl font-title font-medium tracking-tight text-white leading-[1.05] mb-6" data-id="element-619" data-mp="1q71lf897rest">
            Connect
            <br data-id="element-620" data-mp="1zyfeoyaknidi" />
            <span className="text-[#EA580C]" data-id="element-621" data-mp="200098nwvz1bd">Everything.</span>
            <br data-id="element-622" data-mp="240j1uk6lsbem" />
            Architect
            <br data-id="element-623" data-mp="23zhg9lryo6in" />
            Anything.
          </h2>
          <p className="text-lg text-[#9C8E82] leading-relaxed mb-10 max-w-sm" data-id="element-624" data-mp="17a9ss87s7nc3">
            The intelligent infrastructure platform that connects your data,
            systems, and teams—seamlessly.
          </p>
          <div className="flex items-center gap-6" data-id="element-625" data-mp="1gb7xyttyi0w9">
            <button className="px-6 py-3 rounded-full bg-[#EA580C] hover:bg-[#d24e09] text-white font-semibold transition-colors flex items-center gap-2" data-id="element-626" data-mp="1g9n3t5bk0jyx">
              Request a Demo
              <ArrowRight size={18} data-id="element-627" data-mp="2rpbry00jnp1b" />
            </button>
            <button className="text-[#EA580C] font-medium hover:text-white transition-colors flex items-center gap-2" data-id="element-628" data-mp="2woy6ccs3zbbp">
              Explore Platform
              <ArrowRight size={18} data-id="element-629" data-mp="2v8kky32e52go" />
            </button>
          </div>
        </div>

        {/* Right Diagram Area */}
        <div className="relative h-[800px] hidden lg:block" data-id="element-630" data-mp="2qbapo6sblwkx">
          {/* Data Sources Card */}
          <div className="absolute top-[50px] left-[100px] w-[280px] bg-[#221C18]/80 backdrop-blur-md border border-white/10 rounded-2xl p-5 shadow-2xl" data-id="element-631" data-mp="346mlyim4jv3y">
            <div className="flex items-center gap-2 mb-4 text-xs font-bold tracking-widest text-neutral-400 uppercase" data-id="element-632" data-mp="3e5vaux24ujjg">
              <Database size={14} className="text-[#EA580C]" data-id="element-633" data-mp="397aitkx1b663" />
              Data Sources
            </div>
            <div className="space-y-2" data-id="element-634" data-mp="3nvwxdkeu7tw9">
              <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/5" data-id="element-635" data-mp="3ixvi7akj8eq2">
                <Cloud size={18} className="text-neutral-300" data-id="element-636" data-mp="rh2p76m7wpk" />
                <div data-id="element-637" data-mp="pxjp8tkr7k3">
                  <div className="text-sm font-medium text-white" data-id="element-638" data-mp="3ryl85xn43avv">
                    Cloud Storage
                  </div>
                  <div className="text-xs text-neutral-500" data-id="element-639" data-mp="fefpupyfw4yh">Amazon S3</div>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/5" data-id="element-640" data-mp="kchctlyzkof8">
                <Server size={18} className="text-neutral-300" data-id="element-641" data-mp="6dhmm9vts1gy" />
                <div data-id="element-642" data-mp="af34yar4p7yj">
                  <div className="text-sm font-medium text-white" data-id="element-643" data-mp="aebaj399pg9u">Database</div>
                  <div className="text-xs text-neutral-500" data-id="element-644" data-mp="3stc3m32ieicy">PostgreSQL</div>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/5" data-id="element-645" data-mp="1fzkb2cbckhx">
                <LayoutGrid size={18} className="text-neutral-300" data-id="element-646" data-mp="3iwqwnc6xix6h" />
                <div data-id="element-647" data-mp="3ntzjvr6xprel">
                  <div className="text-sm font-medium text-white" data-id="element-648" data-mp="3nvka54m7uo6n">
                    Applications
                  </div>
                  <div className="text-xs text-neutral-500" data-id="element-649" data-mp="39vjblvtfbsuc">12 Connectors</div>
                </div>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between text-xs text-neutral-500 hover:text-white cursor-pointer transition-colors" data-id="element-650" data-mp="1kdcd57ts6oxs">
              View all sources <ArrowRight size={12} data-id="element-651" data-mp="1flkvjmslrtjs" />
            </div>
          </div>

          {/* Data Warehouse Card */}
          <div className="absolute top-[250px] left-[450px] w-[300px] bg-[#221C18]/80 backdrop-blur-md border border-white/10 rounded-2xl p-5 shadow-2xl z-20" data-id="element-652" data-mp="1alq67noh7c0u">
            <div className="flex items-center gap-2 mb-4 text-xs font-bold tracking-widest text-neutral-400 uppercase" data-id="element-653" data-mp="23bf9k7onro5m">
              <Database size={14} className="text-[#EA580C]" data-id="element-654" data-mp="1ydwwc53lymrt" />
              Data Warehouse
            </div>
            <div className="mb-6" data-id="element-655" data-mp="1u9yzk11r9o1x">
              <div className="text-sm font-medium text-white" data-id="element-656" data-mp="1pcz1acb5dpld">
                Analytics Warehouse
              </div>
              <div className="text-xs text-neutral-500" data-id="element-657" data-mp="iwwr5t9t84su">Snowflake</div>
            </div>
            {/* Hex Grid Graphic Placeholder */}
            <div className="h-32 flex items-center justify-center mb-4" data-id="element-658" data-mp="dw8qefvs49lf">
              <div className="grid grid-cols-5 gap-2 opacity-80" data-id="element-659" data-mp="2plyg7q77zhux">
                {Array.from({
                length: 25
              }).map((_, i) => <div key={i} className={`w-3 h-3 rounded-full ${i % 3 === 0 ? 'bg-[#EA580C]' : 'bg-white/20'}`} data-id="element-660" data-mp="2uhvza49vb2be" data-mpi={i} />)}
              </div>
            </div>
            <div className="flex items-center justify-between text-xs" data-id="element-661" data-mp="33dbyl0te69od">
              <div className="flex items-center gap-1.5 text-emerald-400" data-id="element-662" data-mp="38bn6v741gc4h">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" data-id="element-663" data-mp="38dr9llq7b536" />
                Healthy
              </div>
              <div className="text-neutral-500" data-id="element-664" data-mp="3i9uako9bgxoj">99.9% uptime</div>
            </div>
          </div>

          {/* Data Pipelines Card */}
          <div className="absolute top-[40px] right-[20px] w-[280px] bg-[#221C18]/80 backdrop-blur-md border border-white/10 rounded-2xl p-5 shadow-2xl" data-id="element-665" data-mp="3ma4oqo79iueh">
            <div className="flex items-center gap-2 mb-4 text-xs font-bold tracking-widest text-neutral-400 uppercase" data-id="element-666" data-mp="1rx57kqn8hs53">
              <Activity size={14} className="text-[#EA580C]" data-id="element-667" data-mp="190czq6yzsfj7" />
              Data Pipelines
            </div>
            <div className="space-y-2" data-id="element-668" data-mp="142lajkpqf7ip">
              <div className="p-3 rounded-xl bg-white/5 border border-white/5" data-id="element-669" data-mp="141jq4pmtt8ob">
                <div className="text-sm font-medium text-white mb-1" data-id="element-670" data-mp="1hsorbw5ue90r">
                  User Activity Pipeline
                </div>
                <div className="flex items-center justify-between" data-id="element-671" data-mp="1dtz6jiih5p73">
                  <span className="text-xs text-[#EA580C]" data-id="element-672" data-mp="q2k9a45k1y2p">Real-time</span>
                  <svg width="60" height="15" viewBox="0 0 60 15" fill="none" data-id="element-673" data-mp="l3z9g5u7tges">
                    <path d="M0 10 Q 10 0, 20 10 T 40 10 T 60 5" stroke="#EA580C" strokeWidth="1.5" fill="none" data-id="element-674" data-mp="z38cs4xbog1c" />
                  </svg>
                </div>
              </div>
              <div className="p-3 rounded-xl bg-white/5 border border-white/5" data-id="element-675" data-mp="u5yo4rhrc8ek">
                <div className="text-sm font-medium text-white mb-1" data-id="element-676" data-mp="2ajyog3kim02c">
                  ETL – Sales Data
                </div>
                <div className="flex items-center justify-between" data-id="element-677" data-mp="2dzt8ugn3u81t">
                  <span className="text-xs text-blue-400" data-id="element-678" data-mp="2dyi4ofpqyya6">Scheduled</span>
                  <svg width="60" height="15" viewBox="0 0 60 15" fill="none" data-id="element-679" data-mp="2i268qof67bqo">
                    <path d="M0 5 Q 15 15, 30 5 T 60 10" stroke="#60A5FA" strokeWidth="1.5" fill="none" data-id="element-680" data-mp="242f2j9uwazn8" />
                  </svg>
                </div>
              </div>
              <div className="p-3 rounded-xl bg-white/5 border border-white/5" data-id="element-681" data-mp="2wqau605ly1d5">
                <div className="text-sm font-medium text-white mb-1" data-id="element-682" data-mp="2wsdtjasnh6uj">
                  Event Processing
                </div>
                <div className="flex items-center justify-between" data-id="element-683" data-mp="2n0r9qq28xzg6">
                  <span className="text-xs text-[#EA580C]" data-id="element-684" data-mp="2mydheoyyb1cb">Streaming</span>
                  <svg width="60" height="15" viewBox="0 0 60 15" fill="none" data-id="element-685" data-mp="2rxzvf0mlslm6">
                    <path d="M0 12 Q 10 2, 20 12 T 40 8 T 60 12" stroke="#EA580C" strokeWidth="1.5" fill="none" data-id="element-686" data-mp="3fo3y95rywhc4" />
                  </svg>
                </div>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between text-xs text-neutral-500 hover:text-white cursor-pointer transition-colors" data-id="element-687" data-mp="3pdnz4crooqus">
              View all pipelines <ArrowRight size={12} data-id="element-688" data-mp="3gd7ydb2brxtm" />
            </div>
          </div>

          {/* Governance Card */}
          <div className="absolute bottom-[100px] left-[150px] w-[260px] bg-[#221C18]/80 backdrop-blur-md border border-white/10 rounded-2xl p-5 shadow-2xl" data-id="element-689" data-mp="3lcauu748ovpz">
            <div className="flex items-center gap-2 mb-4 text-xs font-bold tracking-widest text-neutral-400 uppercase" data-id="element-690" data-mp="36lv2879hulhs">
              <ShieldCheck size={14} className="text-[#EA580C]" data-id="element-691" data-mp="3bfzdh2pslfoo" />
              Governance
            </div>
            <div className="space-y-4" data-id="element-692" data-mp="2xkx1pyy9kw3y">
              <div data-id="element-693" data-mp="31pd3pp4rbjdw">
                <div className="flex justify-between text-xs mb-1.5" data-id="element-694" data-mp="31ouxq0salqss">
                  <span className="text-white" data-id="element-695" data-mp="2noagh921031e">Data Quality</span>
                  <span className="text-neutral-400" data-id="element-696" data-mp="2snncokesemgn">98%</span>
                </div>
                <div className="h-1 w-full bg-white/10 rounded-full overflow-hidden" data-id="element-697" data-mp="2sppyes8wptfk">
                  <div className="h-full bg-[#EA580C] w-[98%]" data-id="element-698" data-mp="2923lss8i5cej" />
                </div>
              </div>
              <div data-id="element-699" data-mp="244v38qjfz6cr">
                <div className="flex justify-between text-xs mb-1.5" data-id="element-700" data-mp="2j018vceml35k">
                  <span className="text-white" data-id="element-701" data-mp="2j1cfdjzggp0d">Compliance</span>
                  <span className="text-neutral-400" data-id="element-702" data-mp="2dzwrlk5w84ea">100%</span>
                </div>
                <div className="h-1 w-full bg-white/10 rounded-full overflow-hidden" data-id="element-703" data-mp="2s07hajvgvcxv">
                  <div className="h-full bg-[#EA580C] w-full" data-id="element-704" data-mp="2n1w88bczx6gp" />
                </div>
              </div>
              <div data-id="element-705" data-mp="31q95rcrqexdb">
                <div className="flex justify-between text-xs mb-1.5" data-id="element-706" data-mp="31phbc59vf5om">
                  <span className="text-white" data-id="element-707" data-mp="2wrpldgssdtn2">Access Control</span>
                  <span className="text-emerald-400" data-id="element-708" data-mp="2wtissyb84hw4">Active</span>
                </div>
                <div className="h-1 w-full bg-white/10 rounded-full overflow-hidden" data-id="element-709" data-mp="3argtx20jahto">
                  <div className="h-full bg-emerald-400 w-full" data-id="element-710" data-mp="36qx0o9j2zyct" />
                </div>
              </div>
            </div>
            <div className="mt-5 flex items-center justify-between text-xs text-neutral-500 hover:text-white cursor-pointer transition-colors" data-id="element-711" data-mp="3dqpdlh6a5p9i">
              View governance <ArrowRight size={12} data-id="element-712" data-mp="3npye6svni24u" />
            </div>
          </div>

          {/* Integrations Card */}
          <div className="absolute bottom-[40px] left-[480px] w-[240px] bg-[#221C18]/80 backdrop-blur-md border border-white/10 rounded-2xl p-5 shadow-2xl" data-id="element-713" data-mp="3sihtpvdwf7ca">
            <div className="flex items-center gap-2 mb-4 text-xs font-bold tracking-widest text-neutral-400 uppercase" data-id="element-714" data-mp="5cqisiv1o9xl">
              <Share2 size={14} className="text-[#EA580C]" data-id="element-715" data-mp="abtnu3b0yg5k" />
              Integrations
            </div>
            <div className="flex items-center gap-3 mb-4" data-id="element-716" data-mp="fbfzi8b7foc9">
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center" data-id="element-717" data-mp="2c3g7fjsl78s0">
                <img src="https://cdn.simpleicons.org/slack/white" alt="Slack" className="w-5 h-5" data-id="element-718" data-mp="2h2jetaw1hr35" />
              </div>
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center" data-id="element-719" data-mp="y7yf0xqxihpa">
                <img src="https://cdn.simpleicons.org/salesforce/white" alt="Salesforce" className="w-5 h-5" data-id="element-720" data-mp="tg6kkc4njrtv" />
              </div>
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center" data-id="element-721" data-mp="pfwhp8c5phj8">
                <LayoutGrid size={18} className="text-white" data-id="element-722" data-mp="khbinc8n53wd" />
              </div>
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center" data-id="element-723" data-mp="1h59cqdwwx32m">
                <Cloud size={18} className="text-white" data-id="element-724" data-mp="1d5qs7j906u0b" />
              </div>
            </div>
            <div className="flex items-center justify-between text-xs text-neutral-500 hover:text-white cursor-pointer transition-colors" data-id="element-725" data-mp="184t8v3c9btx8">
              Manage integrations <ArrowRight size={12} data-id="element-726" data-mp="138vxzcjrjfru" />
            </div>
          </div>

          {/* Analytics & BI Card */}
          <div className="absolute bottom-[120px] right-[40px] w-[260px] bg-[#221C18]/80 backdrop-blur-md border border-white/10 rounded-2xl p-5 shadow-2xl" data-id="element-727" data-mp="3nwqpy5pmwzdt">
            <div className="flex items-center gap-2 mb-4 text-xs font-bold tracking-widest text-neutral-400 uppercase" data-id="element-728" data-mp="1fmhef8fuhh5f">
              <PieChart size={14} className="text-[#EA580C]" data-id="element-729" data-mp="1kglpo3w58bcb" />
              Analytics & BI
            </div>
            <div className="space-y-2" data-id="element-730" data-mp="1bmgr0yarpvm8">
              <div className="flex items-center justify-between p-2.5 rounded-xl bg-white/5 border border-white/5" data-id="element-731" data-mp="1bnig46omcimy">
                <span className="text-sm text-white" data-id="element-732" data-mp="1zcjh58v4gri7">Executive Dashboard</span>
                <div className="flex items-end gap-0.5 h-4" data-id="element-733" data-mp="23cljzlclfqz2">
                  <div className="w-1 bg-neutral-500 h-[40%]" data-id="element-734" data-mp="1pdu7u4hakfdk" />
                  <div className="w-1 bg-neutral-500 h-[70%]" data-id="element-735" data-mp="1pdc7owridh0d" />
                  <div className="w-1 bg-neutral-500 h-[50%]" data-id="element-736" data-mp="1uc5hof7l6pvs" />
                  <div className="w-1 bg-[#EA580C] h-[100%]" data-id="element-737" data-mp="1uerna9ax2vbd" />
                </div>
              </div>
              <div className="flex items-center justify-between p-2.5 rounded-xl bg-white/5 border border-white/5" data-id="element-738" data-mp="ixry3mjv8wlk">
                <span className="text-sm text-white" data-id="element-739" data-mp="bdr4tfr4kom2">Customer Insights</span>
                <div className="flex items-end gap-0.5 h-4" data-id="element-740" data-mp="bg3hpei108s9">
                  <div className="w-1 bg-[#EA580C] h-[80%]" data-id="element-741" data-mp="6enxtprl48bg" />
                  <div className="w-1 bg-neutral-500 h-[40%]" data-id="element-742" data-mp="kfgtwexjbbgo" />
                  <div className="w-1 bg-neutral-500 h-[60%]" data-id="element-743" data-mp="kckxiv1c4uuc" />
                  <div className="w-1 bg-neutral-500 h-[30%]" data-id="element-744" data-mp="fh5k2478p0yg" />
                </div>
              </div>
              <div className="flex items-center justify-between p-2.5 rounded-xl bg-white/5 border border-white/5" data-id="element-745" data-mp="3nw765bjpmkqt">
                <span className="text-sm text-white" data-id="element-746" data-mp="3jvwy7tccxk9b">Reports & Exports</span>
                <div className="flex items-end gap-0.5 h-4" data-id="element-747" data-mp="1gut715xjmda">
                  <div className="w-1 bg-neutral-500 h-[30%]" data-id="element-748" data-mp="3sv0hoyffs1oc" />
                  <div className="w-1 bg-neutral-500 h-[50%]" data-id="element-749" data-mp="3ssnv27yqh34y" />
                  <div className="w-1 bg-[#EA580C] h-[90%]" data-id="element-750" data-mp="3544nxvrf6weg" />
                  <div className="w-1 bg-neutral-500 h-[60%]" data-id="element-751" data-mp="306ctov3ayy73" />
                </div>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between text-xs text-neutral-500 hover:text-white cursor-pointer transition-colors" data-id="element-752" data-mp="143q7v2oqw8hn">
              View all dashboards <ArrowRight size={12} data-id="element-753" data-mp="1dsrnusvaipvm" />
            </div>
          </div>

          {/* Floating Icons / Nodes */}
          <div className="absolute top-[180px] left-[250px] w-10 h-10 rounded-full bg-[#EA580C]/20 border border-[#EA580C] flex items-center justify-center shadow-[0_0_20px_rgba(234,88,12,0.4)]" data-id="element-754" data-mp="1irl42t1youzh">
            <Database size={16} className="text-[#EA580C]" data-id="element-755" data-mp="q2xckfqmd9dh" />
          </div>
          <div className="absolute top-[200px] right-[320px] w-10 h-10 rounded-full bg-white/10 border border-white/30 flex items-center justify-center" data-id="element-756" data-mp="q59uiswdnjqj">
            <Lock size={16} className="text-white" data-id="element-757" data-mp="z4wst5tt89fd" />
          </div>
          <div className="absolute bottom-[300px] right-[280px] w-10 h-10 rounded-full bg-[#EA580C]/20 border border-[#EA580C] flex items-center justify-center shadow-[0_0_20px_rgba(234,88,12,0.4)]" data-id="element-758" data-mp="26hp4ysnrmgms">
            <PieChart size={16} className="text-[#EA580C]" data-id="element-759" data-mp="3v0tb9cevphgb" />
          </div>
          <div className="absolute bottom-[250px] left-[420px] w-12 h-12 rounded-full bg-[#EA580C] flex items-center justify-center shadow-[0_0_30px_rgba(234,88,12,0.6)] z-30" data-id="element-760" data-mp="3v2w0vvc4d8ki">
            <Share2 size={20} className="text-white" data-id="element-761" data-mp="3m6wsm7kxan5n" />
          </div>
          <div className="absolute bottom-[280px] left-[80px] w-8 h-8 rounded-full bg-[#1A1410] border border-neutral-600 flex items-center justify-center z-30" data-id="element-762" data-mp="3h8lj5xyjieny">
            <Lock size={12} className="text-neutral-400" data-id="element-763" data-mp="37bgu9qdb853g" />
          </div>
        </div>
      </div>
    </section>;
}